"use client";

import { HelpCircle, type LucideIcon, Megaphone, User } from "lucide-react";
import { motion, useAnimation } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import {
  HomeIcon,
  type HomeIconHandle,
  MessageSquareIcon,
  type MessageSquareIconHandle,
} from "@/components/widget/icons/animated";
import { UnreadBadge } from "@/components/widget/unread-badge";
import { useIconHover } from "@/lib/use-icon-hover";
import { cn } from "@/lib/utils";
import type { WidgetFeatures, WidgetTab } from "@/lib/widget-config";

interface BottomNavProps {
  active: WidgetTab;
  features: WidgetFeatures;
  onChange: (tab: WidgetTab) => void;
  /** Unread chat count — badged on the messages tab. 0 hides the badge. */
  unreadCount?: number;
  /** Unread news — a countless dot on the news tab. Only meaningful when the
   *  news tab is enabled and there's an unseen post. */
  newsUnread?: boolean;
}

const TAB_ORDER: WidgetTab[] = ["home", "messages", "help", "news", "profile"];

/**
 * Bottom tab nav — Day 7 refresh.
 *
 * **Floating pill segmented control**: the nav is a rounded card
 * with a glassy backdrop sitting just above the bottom of the
 * panel. The active tab's inner pill **slides between tabs** via
 * Framer Motion's `layoutId`, the active label widens into view
 * while inactive tabs stay icon-only — keeps tap targets large
 * without visual clutter.
 *
 * **Hybrid icons** — `lucide-animated` for Home + Messages (mature
 * animations); `lucide-react` for Help + News (Megaphone &
 * HelpCircle aren't in lucide-animated yet) with a motion-driven
 * `rotate / scale` micro-animation triggered on the **whole pill's**
 * hover, so visitors see motion regardless of where the icon was
 * sourced from.
 */
export function BottomNav({
  active,
  features,
  onChange,
  unreadCount = 0,
  newsUnread = false,
}: BottomNavProps) {
  const t = useTranslations("tabs");
  const tNews = useTranslations("news");
  const homeHover = useIconHover<HomeIconHandle>();
  const messagesHover = useIconHover<MessageSquareIconHandle>();

  const visibleTabs = TAB_ORDER.filter((id) => featureFor(id, features));

  return (
    <div className="px-3 pt-2 pb-2">
      <nav
        // biome-ignore lint/a11y/noNoninteractiveElementToInteractiveRole: intentional nav-as-tablist — both a navigation landmark AND a tab interface (each tab swaps the panel below); kept on <nav> so AT users keep the landmark while the tablist role drives tab/arrow-key semantics
        role="tablist"
        className={cn(
          // NOTE: intentionally NOT `overflow-hidden` — the unread badge's
          // soft glow reaches past the nav's top edge, and clipping it
          // sheared the glow flat. The sliding `layoutId` pill is `inset-0`
          // inside each button (inset ~4px by `p-1`), so it never reaches the
          // rounded edge to overflow; `backdrop-blur` still clips to
          // `rounded-full` on its own.
          // Switched grid → flex so the ACTIVE pill expands to fit its label
          // (`Профіль` was clipped to `Профі.` at 5 equal columns); inactive
          // pills stay icon-only and share the remaining space evenly.
          "relative flex w-full items-center gap-1",
          "rounded-full bg-wx-bg-elevated/70 p-1 backdrop-blur-md",
          "shadow-[inset_0_0_0_1px_rgb(0_0_0/0.04)]",
        )}
      >
        {visibleTabs.map((id) => {
          const isActive = id === active;
          if (id === "home") {
            return (
              <TabPill
                key={id}
                id={id}
                label={t(id)}
                isActive={isActive}
                onClick={() => onChange(id)}
                parentHandlers={homeHover.parentHandlers}
                icon={<HomeIcon ref={homeHover.iconRef} size={16} />}
              />
            );
          }
          if (id === "messages") {
            return (
              <TabPill
                key={id}
                id={id}
                label={t(id)}
                isActive={isActive}
                onClick={() => onChange(id)}
                parentHandlers={messagesHover.parentHandlers}
                icon={<MessageSquareIcon ref={messagesHover.iconRef} size={16} />}
                badgeCount={unreadCount}
              />
            );
          }
          // Help / News / Profile: lucide-react with motion micro-animation
          // triggered by the tab pill's whileHover. (`lucide-animated` has no
          // matching animated glyphs for these yet.)
          const StaticIcon = id === "help" ? HelpCircle : id === "news" ? Megaphone : User;
          return (
            <StaticIconTabPill
              key={id}
              id={id}
              label={t(id)}
              isActive={isActive}
              onClick={() => onChange(id)}
              Icon={StaticIcon}
              showDot={id === "news" && newsUnread}
              dotLabel={tNews("unread")}
            />
          );
        })}
      </nav>
    </div>
  );
}

interface TabPillProps {
  id: WidgetTab;
  label: string;
  isActive: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  parentHandlers?: ReturnType<typeof useIconHover>["parentHandlers"];
  /** Unread-count badge on the icon. 0 hides it; >99 caps to "99+". */
  badgeCount?: number;
}

function TabPill({
  id: _id,
  label,
  isActive,
  onClick,
  icon,
  parentHandlers,
  badgeCount = 0,
}: TabPillProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      {...parentHandlers}
      className={cn(
        "relative z-10 flex items-center justify-center gap-1.5 rounded-full px-3 py-2",
        "transition-colors duration-150",
        // Active pill grows to fit its label; inactive pills hug the icon
        // and share the leftover space evenly so the bar stays balanced.
        isActive ? "flex-[2_2_0%] text-wx-fg" : "flex-1 text-wx-fg-muted hover:text-wx-fg",
      )}
    >
      {isActive && (
        <motion.span
          layoutId="bottom-nav-pill"
          className="absolute inset-0 -z-10 rounded-full bg-wx-bg shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
          transition={{ type: "spring", stiffness: 420, damping: 36 }}
        />
      )}
      {/* `inline-flex` (not `flex`) so the wrapper hugs the icon — otherwise
          it's a full-width block and the badge anchors to the button edge,
          detached from the icon. */}
      <span className="relative inline-flex shrink-0 items-center justify-center">
        {icon}
        <UnreadBadge count={badgeCount} size="sm" className="absolute -top-1.5 -right-2 z-20" />
      </span>
      <motion.span
        animate={{
          width: isActive ? "auto" : 0,
          opacity: isActive ? 1 : 0,
          marginLeft: isActive ? 0 : -4,
        }}
        initial={false}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="overflow-hidden text-[11px] font-semibold tracking-tight whitespace-nowrap"
      >
        {label}
      </motion.span>
    </button>
  );
}

/**
 * Variant for static lucide-react icons. Uses a `useAnimation`
 * controls handle + parent `whileHover` to drive a small
 * rotate/scale pulse on the icon, giving feature parity with the
 * lucide-animated ones until the package ships matching glyphs.
 */
function StaticIconTabPill({
  id: _id,
  label,
  isActive,
  onClick,
  Icon,
  showDot = false,
  dotLabel,
}: {
  id: WidgetTab;
  label: string;
  isActive: boolean;
  onClick: () => void;
  Icon: LucideIcon;
  /** Countless unread dot on the icon (news tab). */
  showDot?: boolean;
  dotLabel?: string;
}) {
  const controls = useAnimation();
  useEffect(() => {
    if (isActive) controls.start({ scale: [1, 1.15, 1] });
  }, [isActive, controls]);

  return (
    <motion.button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      onHoverStart={() => controls.start({ rotate: [0, -10, 10, 0] })}
      className={cn(
        "relative z-10 flex items-center justify-center gap-1.5 rounded-full px-3 py-2",
        "transition-colors duration-150",
        // Active pill grows to fit its label; inactive pills hug the icon
        // and share the leftover space evenly so the bar stays balanced.
        isActive ? "flex-[2_2_0%] text-wx-fg" : "flex-1 text-wx-fg-muted hover:text-wx-fg",
      )}
    >
      {isActive && (
        <motion.span
          layoutId="bottom-nav-pill"
          className="absolute inset-0 -z-10 rounded-full bg-wx-bg shadow-[0_2px_8px_rgba(0,0,0,0.08)]"
          transition={{ type: "spring", stiffness: 420, damping: 36 }}
        />
      )}
      <span className="relative inline-flex shrink-0 items-center justify-center">
        <motion.span animate={controls} transition={{ duration: 0.4 }}>
          <Icon size={16} strokeWidth={isActive ? 2.2 : 1.8} />
        </motion.span>
        <UnreadBadge
          count={0}
          dot={showDot}
          label={dotLabel}
          size="sm"
          className="absolute -top-1.5 -right-2 z-20"
        />
      </span>
      <motion.span
        animate={{
          width: isActive ? "auto" : 0,
          opacity: isActive ? 1 : 0,
          marginLeft: isActive ? 0 : -4,
        }}
        initial={false}
        transition={{ duration: 0.2, ease: "easeOut" }}
        className="overflow-hidden text-[11px] font-semibold tracking-tight whitespace-nowrap"
      >
        {label}
      </motion.span>
    </motion.button>
  );
}

function featureFor(id: WidgetTab, features: WidgetFeatures): boolean {
  if (id === "home") return features.home;
  if (id === "messages") return features.messenger;
  if (id === "help") return features.help;
  if (id === "news") return features.news;
  return !!features.profile;
}
